import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Truck,
  Package,
  Search,
  Filter,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertCircle,
  QrCode,
  ArrowRight,
  Eye,
  FileText,
  User,
  Calendar,
  MapPin,
  Phone,
  RefreshCw,
  X,
  Plus,
  Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    DRAFT: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
    CONFIRMED: { color: 'bg-blue-100 text-blue-700', label: 'Confirmed' },
    IN_TRANSIT: { color: 'bg-purple-100 text-purple-700', label: 'In Transit' },
    RECEIVED: { color: 'bg-green-100 text-green-700', label: 'Received' },
    GRN_COMPLETED: { color: 'bg-emerald-100 text-emerald-700', label: 'GRN Done' }
  };
  
  const { color, label } = config[status] || { color: 'bg-gray-100 text-gray-700', label: status };
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>
      {label}
    </span>
  );
};

// Manifest Card Component
const ManifestCard = ({ manifest, onClick, onReceive }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden">
    <div className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-800 text-sm">{manifest.manifest_id}</h3>
          <p className="text-xs text-gray-500">{manifest.pickup_date}</p>
        </div>
        <StatusBadge status={manifest.status} />
      </div>
      
      <div className="flex items-center gap-2 mb-3">
        <User className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">{manifest.engineer_name}</span>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-lg font-bold text-gray-800">{manifest.lead_count || 0}</p>
          <p className="text-xs text-gray-500">Devices</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-lg font-bold text-[#2b3380]">₹{((manifest.total_value || 0) / 1000).toFixed(1)}K</p>
          <p className="text-xs text-gray-500">Value</p>
        </div>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={() => onClick(manifest)}
          className="flex-1 py-2 text-sm font-medium text-[#2b3380] border border-[#2b3380] rounded-lg hover:bg-[#2b3380]/5 transition-colors flex items-center justify-center gap-1"
        >
          <Eye className="w-4 h-4" />
          View
        </button>
        {manifest.status === 'IN_TRANSIT' && (
          <button
            onClick={() => onReceive(manifest)}
            className="flex-1 py-2 text-sm font-medium text-white bg-[#2b3380] rounded-lg hover:bg-[#232a66] transition-colors flex items-center justify-center gap-1"
          >
            <Package className="w-4 h-4" />
            Receive
          </button>
        )}
      </div>
    </div>
  </div>
);

// Manifest Detail Modal
const ManifestDetailModal = ({ manifest, onClose, onReceive }) => {
  if (!manifest) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800">{manifest.manifest_id}</h2>
            <p className="text-sm text-gray-500">Buyback Manifest</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={manifest.status} />
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Engineer Info */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#2b3380]/10 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-[#2b3380]" />
              </div>
              <div>
                <p className="font-semibold text-gray-800">{manifest.engineer_name}</p>
                <p className="text-sm text-gray-500">{manifest.engineer_id}</p>
              </div>
            </div>
          </div>
          
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-blue-50 rounded-xl">
              <p className="text-2xl font-bold text-[#2b3380]">{manifest.lead_count || 0}</p>
              <p className="text-xs text-gray-600">Total Devices</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-xl">
              <p className="text-2xl font-bold text-green-600">₹{((manifest.total_value || 0) / 1000).toFixed(1)}K</p>
              <p className="text-xs text-gray-600">Total Value</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-xl">
              <p className="text-2xl font-bold text-purple-600">{manifest.pickup_date}</p>
              <p className="text-xs text-gray-600">Pickup Date</p>
            </div>
          </div>
          
          {/* QR Code */}
          {manifest.qr_code && (
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-white border border-gray-200 rounded-xl">
                <img 
                  src={`data:image/png;base64,${manifest.qr_code}`} 
                  alt="Manifest QR" 
                  className="w-32 h-32"
                />
                <p className="text-center text-xs text-gray-500 mt-2">Scan to verify</p>
              </div>
            </div>
          )}
          
          {/* Device List */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Devices ({manifest.leads?.length || 0})</h3>
            <div className="space-y-2">
              {manifest.leads?.map((lead, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-white rounded-lg">
                    <Smartphone className="w-5 h-5 text-[#2b3380]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 text-sm truncate">
                      {lead.device_brand} {lead.device_model}
                    </p>
                    <p className="text-xs text-gray-500">{lead.lead_id} • {lead.pq_grade}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-800">₹{(lead.final_price || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-400">{lead.customer_name}</p>
                  </div>
                </div>
              ))}
              
              {(!manifest.leads || manifest.leads.length === 0) && (
                <p className="text-center text-gray-400 py-4">No devices in manifest</p>
              )}
            </div>
          </div>
          
          {/* Timeline */}
          <div className="mt-6">
            <h3 className="font-semibold text-gray-800 mb-3">Timeline</h3>
            <div className="space-y-3">
              {manifest.created_at && (
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full" />
                  <p className="text-sm text-gray-600">Created: {new Date(manifest.created_at).toLocaleString()}</p>
                </div>
              )}
              {manifest.confirmed_at && (
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-blue-500 rounded-full" />
                  <p className="text-sm text-gray-600">Confirmed: {new Date(manifest.confirmed_at).toLocaleString()}</p>
                </div>
              )}
              {manifest.dispatched_at && (
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-purple-500 rounded-full" />
                  <p className="text-sm text-gray-600">Dispatched: {new Date(manifest.dispatched_at).toLocaleString()}</p>
                </div>
              )}
              {manifest.received_at && (
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                  <p className="text-sm text-gray-600">Received: {new Date(manifest.received_at).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          {manifest.status === 'IN_TRANSIT' && (
            <button
              onClick={() => onReceive(manifest)}
              className="flex-1 py-2.5 text-sm font-medium text-white bg-[#2b3380] rounded-lg hover:bg-[#232a66] flex items-center justify-center gap-2"
            >
              <Package className="w-4 h-4" />
              Receive & Create GRN
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function BuybackManifests({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [manifests, setManifests] = useState([]);
  const [filteredManifests, setFilteredManifests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchManifests();
    
    // Check if specific manifest ID in URL
    const manifestId = searchParams.get('id');
    if (manifestId) {
      fetchManifestDetails(manifestId);
    }
  }, [searchParams]);

  useEffect(() => {
    filterManifests();
  }, [manifests, searchQuery, statusFilter]);

  const fetchManifests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/warehouse/manifests?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setManifests(data.manifests || []);
      }
    } catch (error) {
      console.error('Error fetching manifests:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchManifestDetails = async (manifestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/warehouse/manifest/${manifestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedManifest(data.manifest);
      }
    } catch (error) {
      console.error('Error fetching manifest details:', error);
    }
  };

  const filterManifests = () => {
    let filtered = [...manifests];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m => 
        m.manifest_id?.toLowerCase().includes(query) ||
        m.engineer_name?.toLowerCase().includes(query)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(m => m.status === statusFilter);
    }
    
    setFilteredManifests(filtered);
  };

  const handleReceive = (manifest) => {
    navigate(`/warehouse/grn?manifest=${manifest.manifest_id}`);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchManifests();
  };

  // Stats
  const stats = {
    total: manifests.length,
    inTransit: manifests.filter(m => m.status === 'IN_TRANSIT').length,
    received: manifests.filter(m => m.status === 'RECEIVED').length,
    completed: manifests.filter(m => m.status === 'GRN_COMPLETED').length
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Buyback Manifests</h1>
            <p className="text-sm text-gray-500">Engineer pickup manifests management</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-gray-500 hover:text-[#2b3380] hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-gray-500">Total Manifests</p>
            <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
            <p className="text-sm text-purple-600">In Transit</p>
            <p className="text-2xl font-bold text-purple-700">{stats.inTransit}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-100">
            <p className="text-sm text-green-600">Received</p>
            <p className="text-2xl font-bold text-green-700">{stats.received}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
            <p className="text-sm text-emerald-600">GRN Done</p>
            <p className="text-2xl font-bold text-emerald-700">{stats.completed}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search manifest ID or engineer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
              />
            </div>
            
            {/* Status Filter */}
            <div className="flex gap-2">
              {[
                { value: 'all', label: 'All' },
                { value: 'IN_TRANSIT', label: 'In Transit' },
                { value: 'RECEIVED', label: 'Received' },
                { value: 'GRN_COMPLETED', label: 'GRN Done' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === opt.value
                      ? 'bg-[#2b3380] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Manifest Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380]"></div>
          </div>
        ) : filteredManifests.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <Truck className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Manifests Found</h3>
            <p className="text-gray-500">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Manifests will appear here when engineers dispatch pickups'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredManifests.map(manifest => (
              <ManifestCard
                key={manifest.manifest_id}
                manifest={manifest}
                onClick={(m) => setSelectedManifest(m)}
                onReceive={handleReceive}
              />
            ))}
          </div>
        )}

        {/* Manifest Detail Modal */}
        {selectedManifest && (
          <ManifestDetailModal
            manifest={selectedManifest}
            onClose={() => setSelectedManifest(null)}
            onReceive={handleReceive}
          />
        )}
      </div>
    </Layout>
  );
}

export default BuybackManifests;
